import { useEffect, useState } from "react";
import { Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import OrdersTab from "@/components/admin/OrdersTab";
import ServicesTab from "@/components/admin/ServicesTab";
import SettingsTab from "@/components/admin/SettingsTab";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
      toast.success("Bem-vindo de volta!");
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível entrar."));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-input bg-white px-4 py-3 text-sm outline-none focus:border-accent";

  return (
    <main className="flex min-h-screen items-center justify-center bg-paper px-6" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl border border-line bg-white p-10 shadow-[0_32px_60px_-32px_rgba(17,17,17,0.2)]">
        <div className="flex justify-center">
          <Logo />
        </div>
        <h1 className="mt-8 text-center font-display text-2xl font-extrabold tracking-tight">Painel do estúdio</h1>
        <p className="mt-2 text-center text-sm text-ink-2">Entre para gerenciar serviços e pedidos.</p>
        <div className="mt-8 flex flex-col gap-3">
          <input
            data-testid="admin-login-email-input"
            type="email"
            required
            placeholder="E-mail do administrador"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
          />
          <input
            data-testid="admin-login-password-input"
            type="password"
            required
            placeholder="Senha"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputCls}
          />
          <button
            data-testid="admin-login-submit-button"
            type="submit"
            disabled={loading}
            className="btn-lift mt-2 flex items-center justify-center gap-2 rounded-full bg-ink py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-paper hover:bg-accent disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Entrar
          </button>
        </div>
      </form>
    </main>
  );
}

const TABS = [
  ["pedidos", "Pedidos"],
  ["servicos", "Serviços"],
  ["config", "Configurações"],
];

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("pedidos");

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(null);
  };

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper" data-testid="admin-loading">
        <Loader2 className="h-6 w-6 animate-spin text-ink-3" />
      </div>
    );
  }

  if (!user) return <Login onLogin={setUser} />;

  return (
    <main className="min-h-screen bg-paper text-ink" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 border-b border-line bg-paper/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4 sm:px-10">
          <div className="flex items-center gap-4">
            <a href="/" data-testid="admin-logo-link" aria-label="Voltar ao site">
              <Logo />
            </a>
            <span className="hidden font-mono text-[10px] uppercase tracking-[0.2em] text-ink-3 sm:block">
              Painel do estúdio
            </span>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="btn-lift flex items-center gap-2 rounded-full border border-ink px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-ink hover:text-paper"
          >
            <LogOut className="h-3.5 w-3.5" /> Sair
          </button>
        </div>
        <div className="mx-auto flex max-w-6xl gap-2 px-6 sm:px-10">
          {TABS.map(([id, label]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`-mb-px border-b-2 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
                tab === id ? "border-accent text-accent" : "border-transparent text-ink-2 hover:text-ink"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </header>
      <section className="mx-auto max-w-6xl px-6 py-10 sm:px-10">
        {tab === "pedidos" && <OrdersTab />}
        {tab === "servicos" && <ServicesTab />}
        {tab === "config" && <SettingsTab />}
      </section>
    </main>
  );
}
